import * as React from 'react';
import { Plus } from 'lucide-react';
import { WorkspaceButton, WorkspaceDialog } from '@/components/ui/workspace';

/**
 * Adding a requirement to a module draft (T-0141, E11-F02 scenario 1).
 *
 * Two entry points, both asking for text only: the server assigns the
 * number when the requirement is saved (amendment 01 Decision 32), so
 * there is no number field anywhere.
 *
 *   - `AddRequirementRow` sits at the end of each sub-category in the
 *     grid (prototype `.add-row`) and adds to that sub-category.
 *   - `AddRequirementDialog` also asks for the sub-category, for a first
 *     requirement in an empty module or a new sub-category.
 */

const TEXTAREA =
  'block min-h-14 w-full resize-y rounded-chip border border-ink-300 bg-paper-0 px-2 py-1.5 text-body-sm text-ink-900 outline outline-2 outline-offset-1 outline-accent-amber-600 disabled:opacity-60';

export interface AddRequirementRowProps {
  subcategory: string;
  /** This row's form is open. */
  active: boolean;
  busy: boolean;
  /** Refusal from the last save, shown under the field. */
  error: string | null;
  onStart: () => void;
  onCancel: () => void;
  onSubmit: (text: string) => void;
}

export function AddRequirementRow({
  subcategory,
  active,
  busy,
  error,
  onStart,
  onCancel,
  onSubmit,
}: AddRequirementRowProps) {
  const ref = React.useRef<HTMLTextAreaElement>(null);
  React.useEffect(() => {
    if (active) ref.current?.focus({ preventScroll: true });
  }, [active]);

  if (!active) {
    return (
      <button
        type="button"
        onClick={onStart}
        className="inline-flex items-center gap-1.5 rounded-chip px-1.5 py-1 text-meta font-medium text-ink-500 hover:bg-ink-100 hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
      >
        <Plus className="h-3 w-3" aria-hidden="true" />
        Add requirement to {subcategory}
      </button>
    );
  }

  const submit = () => {
    const text = ref.current?.value.trim() ?? '';
    if (text) onSubmit(text);
  };

  return (
    <div className="py-1.5">
      <textarea
        ref={ref}
        aria-label={`New requirement in ${subcategory}`}
        placeholder="New requirement…"
        disabled={busy}
        maxLength={2000}
        onKeyDown={(e) => {
          // Nothing typed here may reach the grid or page shortcuts.
          e.stopPropagation();
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
        className={TEXTAREA}
      />
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <WorkspaceButton variant="primary" disabled={busy} onClick={submit}>
          {busy ? 'Saving…' : 'Add to draft'}
        </WorkspaceButton>
        <WorkspaceButton variant="tertiary" disabled={busy} onClick={onCancel}>
          Cancel
        </WorkspaceButton>
        <span className="text-meta text-ink-500">The number is assigned when it is saved.</span>
      </div>
      {error ? (
        <p role="alert" className="mt-1.5 text-meta text-posture-critical">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export interface AddRequirementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  moduleName: string;
  /** Sub-categories the module already has, offered as suggestions. */
  subcategories: ReadonlyArray<string>;
  busy: boolean;
  error: string | null;
  onSubmit: (subcategory: string, text: string) => void;
}

const FIELD =
  'block w-full rounded-card border border-ink-200 bg-paper-0 px-3 text-body text-ink-900 placeholder:text-ink-300 hover:border-ink-300 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-accent-amber-600';

export function AddRequirementDialog({
  open,
  onOpenChange,
  moduleName,
  subcategories,
  busy,
  error,
  onSubmit,
}: AddRequirementDialogProps) {
  const [subcategory, setSubcategory] = React.useState('General');
  const [text, setText] = React.useState('');
  const listId = React.useId();

  React.useEffect(() => {
    if (!open) return;
    setSubcategory(subcategories[0] ?? 'General');
    setText('');
    // Only when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const canSubmit = subcategory.trim().length > 0 && text.trim().length > 0 && !busy;
  const submit = () => {
    if (canSubmit) onSubmit(subcategory.trim(), text.trim());
  };

  return (
    <WorkspaceDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Add requirement"
      description={`Added to the draft of ${moduleName}. The number is assigned when it is saved.`}
      actions={
        <>
          <WorkspaceButton size="md" onClick={() => onOpenChange(false)}>
            Cancel
          </WorkspaceButton>
          <WorkspaceButton size="md" variant="primary" disabled={!canSubmit} onClick={submit}>
            {busy ? 'Saving…' : 'Add to draft'}
          </WorkspaceButton>
        </>
      }
    >
      <div>
        <label htmlFor="rl-add-sub" className="mb-1.5 block text-meta font-medium text-ink-700">
          Sub-category <span className="font-normal text-posture-critical">*</span>
        </label>
        <input
          id="rl-add-sub"
          list={listId}
          value={subcategory}
          onChange={(e) => setSubcategory(e.target.value)}
          maxLength={120}
          autoComplete="off"
          className={`${FIELD} h-9`}
        />
        <datalist id={listId}>
          {subcategories.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
        <p className="mt-1.5 text-meta text-ink-500">
          Pick an existing sub-category or type a new one.
        </p>
      </div>
      <div>
        <label htmlFor="rl-add-text" className="mb-1.5 block text-meta font-medium text-ink-700">
          Requirement <span className="font-normal text-posture-critical">*</span>
        </label>
        <textarea
          id="rl-add-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          autoFocus
          maxLength={2000}
          placeholder="The system shall…"
          className={`${FIELD} min-h-24 resize-y py-2 leading-5`}
        />
      </div>
      {error ? (
        <p role="alert" className="text-meta text-posture-critical">
          {error}
        </p>
      ) : null}
    </WorkspaceDialog>
  );
}
